import React, { useState } from 'react';
import { api } from '../services/api';
import { X, Car, DollarSign, User, FileText } from 'lucide-react';

function CreateDealModal({ contact, onClose, onDealCreated }) {
  const [formData, setFormData] = useState({
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: new Date().getFullYear(),
    vehicle_vin: '',
    sale_price: '',
    trade_in_value: '',
    down_payment: '',
    financing_amount: '',
    gross_profit: '',
    sales_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const dealData = {
        contact_id: contact.id,
        vehicle: {
          make: formData.vehicle_make,
          model: formData.vehicle_model,
          year: parseInt(formData.vehicle_year),
          vin: formData.vehicle_vin || null
        },
        economics: {
          sale_price: parseFloat(formData.sale_price) || 0,
          trade_in_value: parseFloat(formData.trade_in_value) || null,
          down_payment: parseFloat(formData.down_payment) || null,
          financing_amount: parseFloat(formData.financing_amount) || null,
          gross_profit: parseFloat(formData.gross_profit) || null
        },
        sales_notes: formData.sales_notes || null
      };

      const result = await api.request('/api/deals/', {
        method: 'POST',
        body: JSON.stringify(dealData)
      });

      onDealCreated(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create deal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <Car size={24} style={{ color: 'var(--brand-primary, #3b82f6)' }} />
            <h2 style={styles.title}>Create Deal</h2>
          </div>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div style={styles.contactInfo}>
          <User size={16} />
          <span>for {contact.first_name} {contact.last_name}</span>
        </div>

        {error && (
          <div style={styles.error}>{error}</div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Vehicle Details</h3>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Make *</label>
                <input
                  type="text"
                  name="vehicle_make"
                  value={formData.vehicle_make}
                  onChange={handleChange}
                  placeholder="e.g. Toyota"
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Model *</label>
                <input
                  type="text"
                  name="vehicle_model"
                  value={formData.vehicle_model}
                  onChange={handleChange}
                  placeholder="e.g. Camry"
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.fieldSmall}>
                <label style={styles.label}>Year *</label>
                <input
                  type="number"
                  name="vehicle_year"
                  value={formData.vehicle_year}
                  onChange={handleChange}
                  required
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>VIN</label>
              <input
                type="text"
                name="vehicle_vin"
                value={formData.vehicle_vin}
                onChange={handleChange}
                placeholder="Vehicle Identification Number"
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Deal Economics</h3>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Sale Price *</label>
                <div style={styles.inputWithIcon}>
                  <DollarSign size={16} style={styles.inputIcon} />
                  <input
                    type="number"
                    name="sale_price"
                    value={formData.sale_price}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                    style={{ ...styles.input, paddingLeft: '36px' }}
                  />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Trade-in Value</label>
                <div style={styles.inputWithIcon}>
                  <DollarSign size={16} style={styles.inputIcon} />
                  <input
                    type="number"
                    name="trade_in_value"
                    value={formData.trade_in_value}
                    onChange={handleChange}
                    placeholder="0.00"
                    style={{ ...styles.input, paddingLeft: '36px' }}
                  />
                </div>
              </div>
            </div>
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Down Payment</label>
                <div style={styles.inputWithIcon}>
                  <DollarSign size={16} style={styles.inputIcon} />
                  <input
                    type="number"
                    name="down_payment"
                    value={formData.down_payment}
                    onChange={handleChange}
                    placeholder="0.00"
                    style={{ ...styles.input, paddingLeft: '36px' }}
                  />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Financing Amount</label>
                <div style={styles.inputWithIcon}>
                  <DollarSign size={16} style={styles.inputIcon} />
                  <input
                    type="number"
                    name="financing_amount"
                    value={formData.financing_amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    style={{ ...styles.input, paddingLeft: '36px' }}
                  />
                </div>
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Gross Profit</label>
              <div style={styles.inputWithIcon}>
                <DollarSign size={16} style={styles.inputIcon} />
                <input
                  type="number"
                  name="gross_profit"
                  value={formData.gross_profit}
                  onChange={handleChange}
                  placeholder="0.00"
                  style={{ ...styles.input, paddingLeft: '36px' }}
                />
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Notes</h3>
            <div style={styles.field}>
              <label style={styles.label}>Sales Notes</label>
              <textarea
                name="sales_notes"
                value={formData.sales_notes}
                onChange={handleChange}
                placeholder="Add any notes about this deal..."
                rows={3}
                style={styles.textarea}
              />
            </div>
          </div>

          <div style={styles.actions}>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={styles.submitButton}
            >
              {loading ? 'Creating...' : 'Create Deal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#0f172a',
    borderRadius: '16px',
    border: '1px solid #334155',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #334155'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#f1f5f9'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  contactInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#1e293b',
    color: '#94a3b8',
    fontSize: '14px'
  },
  error: {
    margin: '16px 24px',
    padding: '12px 16px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    color: '#ef4444',
    fontSize: '14px'
  },
  form: {
    padding: '24px'
  },
  section: {
    marginBottom: '24px'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#f1f5f9',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  row: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px'
  },
  field: {
    flex: 1
  },
  fieldSmall: {
    width: '100px'
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#94a3b8',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  inputWithIcon: {
    position: 'relative'
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '14px',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #334155'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  submitButton: {
    padding: '10px 24px',
    backgroundColor: 'var(--brand-primary, #3b82f6)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer'
  }
};

export default CreateDealModal;
